import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';

import { cst, cstIpi, cstPisCofins } from "../utils/options";

const fieldClasses = 'field col-12';
const labelClasses = 'text-gray-800 text-base';
const inputClasses = 'w-50';
const sectionTitleClasses = "text-black-alpha-90 text-xl font-medium mb-4 mt-5"
        
const ProductDetailsForm = ({
  productData,
  setProductData
}) => {
  const showIpiAliquit = 
    productData?.ipi?.cst.number === 0 ||
    productData?.ipi?.cst.number === 49 ||
    productData?.ipi?.cst.number === 50 ||
    productData?.ipi?.cst.number === 99;

  const showPisAliquot = 
    productData?.pis?.cst?.number < 4 ||
    productData?.pis?.cst?.number > 9;

  const showCofinsAliquot = 
    productData?.cofins?.cst?.number < 4 ||
    productData?.cofins?.cst?.number > 9;

  return (
    <>
      <h1 className="text-gray-900 text-2xl font-semibold">Detalhes do produto</h1>

      <h2 className={sectionTitleClasses}>Informações gerais</h2>
      <div className="grid">
        <div className={fieldClasses}>
          <label 
            htmlFor="product" 
            className={labelClasses}
          >
            Produto
          </label>

          <InputText
            id="product"
            value={productData?.product?.description}
            disabled
            className="w-full"
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="quantity" 
            className={`${labelClasses} w-full`}
          >
            Quantidade
          </label>

          <InputNumber
            id="quantity"
            value={productData?.quantity}
            min={0}
            useGrouping={false}
            disabled
            className={inputClasses}
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="unitaryValue" 
            className={`${labelClasses} w-full`}
          >
            Valor unitário
          </label>

          <InputNumber
            id="unitaryValue"
            value={productData?.unitaryValue}
            locale="pt-BR"
            mode="decimal" 
            maxFractionDigits={5}
            min={0}
            onChange={e => setProductData({
              ...productData,
              unitaryValue: e.value
            })}
            className={inputClasses}
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="unit" 
            className={`${labelClasses} w-full`}
          >
            Unidade
          </label>

          <InputText
            id="unit"
            value={productData?.unit?.symbol}
            disabled
            className={inputClasses}
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="totalValue" 
            className={`${labelClasses} w-full`}
          >
            Valor total
          </label>

          <InputNumber
            id="totalValue"
            value={productData?.unitaryValue * productData?.quantity || 0}
            disabled
            locale="pt-BR"
            mode="decimal" 
            maxFractionDigits={5}
            className={inputClasses}
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="cfop" 
            className={`${labelClasses} w-full`}
          >
            CFOP
          </label>

          <InputNumber
            id="cfop"
            value={productData?.cfop}
            useGrouping={false}
            onChange={e => setProductData({
              ...productData,
              cfop: e.value
            })}
            className={inputClasses}
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="taxBenefitCode" 
            className={`${labelClasses} w-full flex align-items-center`}
          >
            Código de Benefício Físcal 
            <span className="text-xs text-gray-400 ml-1">(opcional)</span>
          </label>

          <InputText
            id="taxBenefitCode"
            value={productData?.taxBenefitCode}
            onChange={e => setProductData({
              ...productData,
              taxBenefitCode: e.target.value
            })}
            className="w-full"
          />
        </div>
      </div>

      <Divider />

      <h2 className={sectionTitleClasses}>IPI</h2>

      <div className="grid">
        <div className={fieldClasses}>
          <label 
            htmlFor="ipi-cst" 
            className={`${labelClasses} w-full`}
          >
            CST
          </label>

          <Dropdown
            id="ipi-cst"
            value={productData?.ipi?.cst}
            onChange={(e) => {
              setProductData({
                ...productData,
                ipi: {
                  ...productData?.ipi,
                  cst: e.value
                }
              })
            }}
            options={cstIpi} 
            optionLabel="description" 
            placeholder="Selecione uma opção" 
            className="w-full"
            filter
            filterInputAutoFocus
          />
        </div>

        <div className={fieldClasses}>
          <label 
            htmlFor="ipi-legalFrameworkCode" 
            className={`${labelClasses} flex align-items-center`}
          >
            Cód. de enquadramento legal
          </label>

          <InputText
            id="ipi-legalFrameworkCode"
            value={productData?.ipi?.legalFrameworkCode}
            onChange={e => setProductData({
              ...productData,
              ipi: {
                ...productData?.ipi,
                legalFrameworkCode: e.target.value
              }
            })}
          />
        </div>

        {showIpiAliquit && (
          <div className={fieldClasses}>
            <label 
              htmlFor="ipi-taxRate" 
              className={`${labelClasses} flex align-items-center`}
            >
              Alíquota
            </label>

            <InputNumber
              id="ipi-taxRate"
              value={productData?.ipi?.taxRate}
              useGrouping={false}
              max={100}
              maxFractionDigits={2}
              locale="pt-BR"
              onChange={e => setProductData({
                ...productData,
                ipi: {
                  ...productData?.ipi,
                  taxRate: e.value
                }
              })}
              className={inputClasses}
            />
          </div>
        )}
      </div>

      <Divider />

      <h2 className={sectionTitleClasses}>PIS</h2>

      <div className="grid gap-y-4">
        <div className={fieldClasses}>
          <label 
            htmlFor="pis-cst" 
            className={`${labelClasses} w-full`}
          >
            CST
          </label>

          <Dropdown
            id="pis-cst"
            value={productData?.pis?.cst}
            onChange={(e) => {
              setProductData({
                ...productData,
                pis: {
                  ...productData?.pis,
                  cst: e.value
                }
              })
            }}
            options={cstPisCofins} 
            optionLabel="description"
            placeholder="Selecione uma opção" 
            className="w-full"
            filter
            filterInputAutoFocus
          />
        </div>

        {showPisAliquot && (
          <div className={fieldClasses}>
            <label 
              htmlFor="pis-taxRate" 
              className={`${labelClasses} w-full flex align-items-center`}
            >
              Alíquota
            </label>

            <InputNumber
              id="pis-taxRate"
              value={productData?.pis?.taxRate}
              useGrouping={false}
              max={100}
              maxFractionDigits={2}
              locale="pt-BR"
              onChange={e => setProductData({
                ...productData,
                pis: {
                  ...productData?.pis,
                  taxRate: e.value
                }
              })}
              className={inputClasses}
            />
          </div>
        )}
      </div>

      <Divider />

      <h2 className={sectionTitleClasses}>COFINS</h2>

      <div className="grid gap-y-4">
        <div className={fieldClasses}>
          <label 
            htmlFor="cofins-cst" 
            className={`${labelClasses} w-full`}
          >
            CST
          </label>

          <Dropdown
            id="cofins-cst"
            value={productData?.cofins?.cst}
            onChange={(e) => {
              setProductData({
                ...productData,
                cofins: {
                  ...productData?.cofins,
                  cst: e.value
                }
              })
            }}
            options={cstPisCofins} 
            optionLabel="description"
            placeholder="Selecione uma opção" 
            className="w-full"
            filter
            filterInputAutoFocus
          />
        </div>

        {showCofinsAliquot && (
          <div className={fieldClasses}>
            <label 
              htmlFor="cofins-taxRate" 
              className={`${labelClasses} w-full flex align-items-center`}
            >
              Alíquota
            </label>

            <InputNumber
              id="cofins-taxRate"
              value={productData?.cofins?.taxRate}
              useGrouping={false}
              max={100}
              maxFractionDigits={2}
              locale="pt-BR"
              onChange={e => setProductData({
                ...productData,
                cofins: {
                  ...productData?.cofins,
                  taxRate: e.value
                }
              })}
              className={inputClasses}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ProductDetailsForm;